import React, { useEffect, useState } from 'react';
import { addQuestion, deleteQuestion, editQuestion, getModuleQuestions } from '../../../services/adminService';

import { Icon } from '@iconify/react';
import { decodeData } from '../../../utils/encodeAndEncode';
import { toast } from 'react-toastify';
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from '../../../context/PermissionsContext';

function CourseSurvey() {
    const { courseId, moduleId, contentId } = useParams();
    const { permissions } = usePermissions();
    
    // Decode IDs
    const decodedCourseId = decodeData(courseId);
    const decodedModuleId = decodeData(moduleId);
    const decodedContentId = decodeData(contentId);

    // API data states 
    const [apiQuestions, setApiQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form data states
    const [questionData, setQuestionData] = useState({ 
        question: '',
        question_type: 'survey',
        options: {},
    });

    // UI control states
    const [showModal, setShowModal] = useState(false);

    // Options state
    const [options, setOptions] = useState([{ 
        id: Date.now(), 
        text: '', 
        optionKey: 'option1' 
    }]);

    // Edit mode states
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    // Delete confirmation states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);

    // Validation states
    const [errors, setErrors] = useState({
        question: '',
        options: []
    });

    // Reset errors when modal opens
    useEffect(() => {
        if (showModal) {
            setErrors({
                question: '',
                options: []
            });
        }
    }, [showModal]);

    // Fetch existing questions
    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                if (!decodedModuleId || !decodedContentId) {
                    toast.error('Missing required parameters', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    return;
                }

                const response = await getModuleQuestions({
                    module_id: decodedModuleId,
                    assessment_id: decodedContentId
                });

                if (response.success && response.data.response) {
                    setApiQuestions(response.data.response);
                } else {
                    toast.error('Failed to load survey questions', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            } catch (error) {
                console.error('Error fetching survey questions:', error);
                toast.error('Failed to fetch survey questions', {
                    position: 'top-center',
                    autoClose: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [decodedModuleId, decodedContentId]);

    // Handle question text change
    const handleQuestionChange = (e) => {
        setQuestionData(prev => ({
            ...prev,
            question: e.target.value
        }));
        if (errors.question) {
            setErrors(prev => ({
                ...prev,
                question: ''
            }));
        }
    };

    // Handle option change
    const handleOptionChange = (index, value) => {
        const newOptions = [...options];
        newOptions[index].text = value;
        setOptions(newOptions);

        // Clear error for this option
        if (errors.options[index]) {
            const newOptionErrors = [...errors.options];
            newOptionErrors[index] = '';
            setErrors(prev => ({
                ...prev,
                options: newOptionErrors
            }));
        }

        // Update options data for API
        const optionKey = `option${index + 1}`;
        setQuestionData(prev => ({
            ...prev,
            options: {
                ...prev.options,
                [optionKey]: value
            }
        }));
    };

    // Handle edit button click
    const handleEditClick = (question) => {
        if (!permissions.course_module_content_management_edit) {
            toast.warning("You don't have permission to edit survey questions");
            return;
        }
        setIsEditMode(true);
        setEditingQuestion(question);

        // Set question data
        setQuestionData({
            question: question.question || '',
            question_type: 'survey',
            options: {},
        });

        // Set options with safety checks
        let formattedOptions = [];
        if (question.formatted_options && Array.isArray(question.formatted_options) && question.formatted_options.length > 0) {
            formattedOptions = question.formatted_options.map((opt, index) => ({
                id: Date.now() + index,
                text: opt.content_value || opt.text || opt.value || '',
                optionKey: opt.key || `option${index + 1}`
            }));
        } else {
            // Fallback: create a default option if no options exist
            formattedOptions = [{
                id: Date.now(),
                text: '',
                optionKey: 'option1'
            }];
        }
        setOptions(formattedOptions);

        setShowModal(true);
    };

    // Reset form function
    const resetForm = () => {
        setQuestionData({
            question: '',
            question_type: 'survey',
            options: {},
        });
        setOptions([{ 
            id: Date.now(), 
            text: '', 
            optionKey: 'option1' 
        }]);
        setIsEditMode(false);
        setEditingQuestion(null);
        setErrors({
            question: '',
            options: []
        });
    };

    const validateForm = () => {
        const newErrors = {
            question: '',
            options: []
        };
        let isValid = true;

        // Validate question text
        if (!questionData.question.trim()) {
            newErrors.question = 'Question text is required';
            isValid = false;
        } else if (questionData.question.trim().length > 500) {
            newErrors.question = 'Question text cannot exceed 500 characters';
            isValid = false;
        }

        // Validate options
        const optionErrors = options.map(option => {
            if (!option.text.trim()) {
                isValid = false;
                return 'Option text is required';
            }
            if (option.text.trim().length > 100) {
                isValid = false;
                return 'Option text cannot exceed 100 characters';
            }
            return '';
        });

        // Check if at least one option is provided
        if (options.length === 0) {
            isValid = false;
            newErrors.options = ['At least one option is required'];
        } else {
            newErrors.options = optionErrors;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        try {
            const formData = new FormData();

            if (isEditMode) {
                // Edit mode payload
                formData.append('id', editingQuestion.id);
                formData.append('question_type', 'mcq');
                formData.append('question_text', questionData.question);
                formData.append('assessment_id', decodedContentId);
                formData.append('module_id', decodedModuleId);

                // Convert options to the format expected by the API
                const formattedOptions = {};
                options.forEach(option => {
                    formattedOptions[option.optionKey] = option.text;
                });
                formData.append('options', JSON.stringify(formattedOptions));

                // Add correct_option field - for survey, all options are treated as correct
                const correctOptions = options.map(option => option.text);
                formData.append('correct_option', JSON.stringify(correctOptions));

                // Handle options data - include all original options
                const optionsData = [];

                // Get all original option keys from the editing question with safety checks
                const originalOptionKeys = editingQuestion.formatted_options && Array.isArray(editingQuestion.formatted_options)
                    ? editingQuestion.formatted_options.map(opt => opt.key || opt.optionKey || `option${Math.random()}`)
                    : [];

                // Add current options
                options.forEach(option => {
                    optionsData.push({
                        option_key: option.optionKey,
                        content_type: 'text',
                        text_value: option.text,
                        is_changed: true
                    });
                });

                // Find deleted options and mark them for deletion
                const deletedOptionKeys = originalOptionKeys.filter(originalKey =>
                    !options.some(opt => opt.optionKey === originalKey)
                );

                formData.append('options_data', JSON.stringify(optionsData));
                formData.append('deleted_option_keys', JSON.stringify(deletedOptionKeys));

                const response = await editQuestion(formData);

                if (response && response.success) {
                    toast.success('Survey question updated successfully!', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setShowModal(false);
                    setIsEditMode(false);
                    resetForm();

                    // Refresh questions
                    const questionsResponse = await getModuleQuestions({
                        module_id: decodedModuleId,
                        assessment_id: decodedContentId
                    });
                    if (questionsResponse.success && questionsResponse.data.response) {
                        setApiQuestions(questionsResponse.data.response);
                    }
                } else {
                    toast.error(response?.error || 'Failed to update survey question', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            } else {
                // Add mode payload
                formData.append('module_id', decodedModuleId);
                formData.append('assessment_id', decodedContentId);
                formData.append('question_type', 'mcq');
                formData.append('question_text', questionData.question);

                // Convert options to the format expected by the API
                const formattedOptions = {};
                options.forEach(option => {
                    formattedOptions[option.optionKey] = option.text;
                });
                formData.append('options', JSON.stringify(formattedOptions));

                // Add correct_option field - for survey, all options are treated as correct
                const correctOptions = options.map(option => option.text);
                formData.append('correct_option', JSON.stringify(correctOptions));

                // Handle options data
                const optionsData = options.map(option => ({
                    option_key: option.optionKey,
                    content_type: 'text',
                    text_value: option.text
                }));

                formData.append('options_data', JSON.stringify(optionsData));

                const response = await addQuestion(formData);

                if (response && response.success) {
                    toast.success('Survey question added successfully!', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setShowModal(false);
                    resetForm();

                    // Refresh questions
                    const questionsResponse = await getModuleQuestions({
                        module_id: decodedModuleId,
                        assessment_id: decodedContentId
                    });
                    if (questionsResponse.success && questionsResponse.data.response) {
                        setApiQuestions(questionsResponse.data.response);
                    }
                } else {
                    toast.error(response?.error || 'Failed to add survey question', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'editing' : 'adding'} survey question:`, error);
            toast.error(error.message || `An error occurred while ${isEditMode ? 'editing' : 'adding'} the survey question`, {
                position: 'top-center',
                autoClose: 3000
            });
        }
    };

    const addOption = () => {
        const newOptionIndex = options.length + 1;
        setOptions([
            ...options, 
            { 
                id: Date.now(), 
                text: '', 
                optionKey: `option${newOptionIndex}`
            }
        ]);
    };

    const handleDeleteOption = (index) => {
        const newOptions = options.filter((_, i) => i !== index);
        setOptions(newOptions);
    };

    // Handle delete question
    const handleDeleteClick = (question) => {
        if (!permissions.course_module_content_management_delete) {
            toast.warning("You don't have permission to delete survey questions");
            return;
        }
        setQuestionToDelete(question);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (!questionToDelete) return;

            const response = await deleteQuestion({
                question_id: questionToDelete.id
            });

            if (response && response.success) {
                toast.success('Survey question deleted successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                setApiQuestions(prev => prev.filter(q => q.id !== questionToDelete.id));
            } else {
                toast.error(response?.error || 'Failed to delete survey question', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error deleting survey question:', error);
            toast.error(error.message || 'An error occurred while deleting the survey question', {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            setShowDeleteModal(false);
            setQuestionToDelete(null);
        }
    };

    // Handle add question click
    const handleAddClick = () => {
        if (!permissions.course_module_content_management_create) {
            toast.warning("You don't have permission to create survey questions");
            return;
        }
        setShowModal(true);
    };

    return (
        <>
            <div className="mb-4 px-4 md:px-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Link
                            to={`/admin/courses/module/${courseId}/content/${moduleId}`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 no-underline"
                        >
                            <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
                        </Link>
                        <div>
                            <h4 className="mb-0 text-lg font-semibold text-gray-900">Course Survey</h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Manage survey questions and options for this module.
                            </p>
                        </div>
                    </div>
                    {permissions.course_module_content_management_create && (
                        <button
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#7083FF] hover:bg-[#5f73e6] text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleAddClick}
                            disabled={!permissions.course_module_content_management_create}
                            title={!permissions.course_module_content_management_create ? "You don't have permission to create questions" : "Add new question"}
                        >
                            <Icon icon="fluent:add-24-regular" width={18} height={18} />
                            <span>Add Question</span>
                        </button>
                    )}
                </div>
            </div>

            <div>
                <div className="w-full">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div className="p-4 sm:p-5">
                            {loading ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#7083FF]" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : apiQuestions.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">No survey questions yet. Add your first question to get started.</p>
                                </div>
                            ) : (
                                apiQuestions.map((question, index) => (
                                    <div className="bg-gray-50/50 rounded-xl border border-gray-100 p-4 sm:p-5 mb-4 last:mb-0" key={question.id}>
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#7083FF] text-white text-sm font-semibold shrink-0">
                                                        {index + 1}
                                                    </span>
                                                    <h6 className="text-base font-semibold text-gray-900 break-words">{question.question}</h6>
                                                </div>
                                                <ul className="list-none space-y-2 pl-0">
                                                    {question.formatted_options && Array.isArray(question.formatted_options) && question.formatted_options.length > 0 ? (
                                                        question.formatted_options.map((option) => (
                                                            <li
                                                                key={option.key || option.id || Math.random()}
                                                                className="flex items-center gap-2 text-gray-600 text-sm"
                                                            >
                                                                <Icon icon="fluent:circle-24-regular" className="text-gray-400 shrink-0" width="16" height="16" />
                                                                <span>{option.content_value || option.text || option.value || 'No content'}</span>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="flex items-center gap-2 text-gray-400 text-sm">
                                                            <Icon icon="fluent:info-24-regular" width="16" height="16" />
                                                            <span>No options for this question</span>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg border-2 border-[#7083FF] text-[#7083FF] hover:bg-[#7083FF]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.course_module_content_management_edit ? "You don't have permission to edit questions" : "Edit question"}
                                                    onClick={() => handleEditClick(question)}
                                                    disabled={!permissions.course_module_content_management_edit}
                                                >
                                                    <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.course_module_content_management_delete ? "You don't have permission to delete questions" : "Delete question"}
                                                    onClick={() => handleDeleteClick(question)}
                                                    disabled={!permissions.course_module_content_management_delete}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
                        <div className="p-5 sm:p-6 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="text-lg font-semibold text-gray-900">Delete survey question</h5>
                            <button
                                type="button"
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                onClick={() => { setShowDeleteModal(false); setQuestionToDelete(null); }}
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-5 sm:p-6">
                            <p className="text-gray-600 text-sm">This action cannot be undone. The question and its options will be removed.</p>
                        </div>
                        <div className="p-5 sm:p-6 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                                onClick={() => { setShowDeleteModal(false); setQuestionToDelete(null); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-sm transition-colors"
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Question Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-5 sm:p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
                            <h5 className="text-lg font-semibold text-gray-900">
                                {isEditMode ? 'Edit survey question' : 'Add survey question'}
                            </h5>
                            <button
                                type="button"
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                onClick={() => { setShowModal(false); resetForm(); }}
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-5 sm:p-6 overflow-y-auto flex-1">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Question text *</label>
                                <input
                                    type="text"
                                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF] ${errors.question ? 'border-red-500' : 'border-gray-200'}`}
                                    value={questionData.question}
                                    onChange={handleQuestionChange}
                                    placeholder="Enter your survey question"
                                    maxLength={500}
                                />
                                {errors.question ? (
                                    <p className="text-red-500 text-xs mt-1">{errors.question}</p>
                                ) : (
                                    <p className="text-gray-500 text-xs mt-1">{500 - questionData.question.length} characters remaining</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Options *</label>
                                <div className="space-y-3">
                                    {options.map((option, index) => (
                                        <div className="rounded-xl border border-gray-200 p-3 bg-gray-50/50" key={option.id}>
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs font-medium text-gray-500">Option {index + 1}</span>
                                                <button
                                                    type="button"
                                                    className={`p-1.5 rounded-lg flex items-center justify-center transition-colors ${options.length <= 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`}
                                                    title={options.length <= 1 ? 'At least one option required' : 'Remove option'}
                                                    disabled={options.length <= 1}
                                                    onClick={() => handleDeleteOption(index)}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF] ${errors.options[index] ? 'border-red-500' : 'border-gray-200'}`}
                                                placeholder={`Enter option ${index + 1}`}
                                                value={option.text}
                                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                                maxLength={100}
                                            />
                                            {errors.options[index] ? (
                                                <p className="text-red-500 text-xs mt-1">{errors.options[index]}</p>
                                            ) : (
                                                <p className="text-gray-400 text-xs mt-1">{100 - option.text.length} left</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[#7083FF] text-[#7083FF] font-medium text-sm hover:bg-[#7083FF]/10 transition-colors"
                                    onClick={addOption}
                                >
                                    <Icon icon="fluent:add-24-regular" width="18" height="18" />
                                    Add option
                                </button>
                            </div>
                        </div>
                        <div className="p-5 sm:p-6 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                                onClick={() => { setShowModal(false); resetForm(); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl bg-[#7083FF] hover:bg-[#5f73e6] text-white font-medium text-sm transition-colors"
                                onClick={handleSubmit}
                            >
                                {isEditMode ? 'Update question' : 'Save question'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CourseSurvey;
